/**
 * Normalizes REACT_APP_CLOUDFRONT_S3_URL from runtime config.
 * Accepts hostname only (e.g. assets.aca.example) or https base URL (optional path prefix).
 */
export function normalizeCloudFrontBase(
  cloudFrontEnv: string | undefined,
): string | null {
  const raw = cloudFrontEnv?.trim();
  if (!raw) {
    return null;
  }
  if (/^https?:\/\//i.test(raw)) {
    return raw.replace(/\/+$/, '');
  }
  return `https://${raw.replace(/\/+$/, '')}`;
}

function joinCloudFrontAsset(base: string, filename: string): string {
  return `${base}/${filename.replace(/^\//, '')}`;
}

/**
 * Builds CloudFront logo URL — favicon.svg for every client (same semantics).
 */
export function buildClientLogoUrl(
  _clientName: string | undefined,
  cloudFrontEnv: string | undefined,
): string | null {
  const base = normalizeCloudFrontBase(cloudFrontEnv);
  if (!base) {
    return null;
  }
  return joinCloudFrontAsset(base, 'favicon.svg');
}

/** Logo URLs to preload (CloudFront / env configuration only). */
export function buildClientLogoCandidateUrls(
  clientName: string | undefined,
  cloudFrontEnv: string | undefined,
): string[] {
  const remote = buildClientLogoUrl(clientName, cloudFrontEnv);
  return remote ? [remote] : [];
}

/** Tab favicon: same asset as useClientLogo() — favicon.svg on CloudFront. */
export function buildClientFaviconCandidateUrls(
  clientName: string | undefined,
  cloudFrontEnv: string | undefined,
): string[] {
  return buildClientLogoCandidateUrls(clientName, cloudFrontEnv);
}

export function preloadFirstImageUrl(urls: string[]): Promise<string | null> {
  if (urls.length === 0) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    let index = 0;

    const tryNext = () => {
      if (index >= urls.length) {
        resolve(null);
        return;
      }

      const url = urls[index];
      index += 1;
      const img = new Image();

      img.onload = () => resolve(url);
      img.onerror = () => tryNext();
      img.src = url;
    };

    tryNext();
  });
}

export function resolveFaviconLinkType(url: string): string | undefined {
  if (url.endsWith('.png')) {
    return 'image/png';
  }
  if (url.endsWith('.svg')) {
    return 'image/svg+xml';
  }
  if (url.endsWith('.ico')) {
    return 'image/x-icon';
  }
  return undefined;
}